import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, BookOpen, Activity, TrendingUp, LogOut, Crown, KeyRound } from 'lucide-react';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { AdminExercisesTab } from '../components/admin/AdminExercisesTab';
import { AdminCoursesTab } from '../components/admin/AdminCoursesTab';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminMessagesTab } from '../components/admin/AdminMessagesTab';
import { AdminCommunityTab } from '../components/admin/AdminCommunityTab';
import { AdminContentTab } from '../components/admin/AdminContentTab';
import { AdminSubscriptionsTab } from '../components/admin/AdminSubscriptionsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [exercises, setExercises] = useState([]);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // About / Testimonials
  const [aboutSection, setAboutSection] = useState({ title: '', description: '', video_file: '', youtube_url: '', image_file: '' });
  const [uploadingAboutImage, setUploadingAboutImage] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [showTestimonialDialog, setShowTestimonialDialog] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({ name: '', role: '', content: '', rating: 5, image_url: '' });
  const [aboutVideoSource, setAboutVideoSource] = useState('youtube');
  const [uploadingAboutVideo, setUploadingAboutVideo] = useState(false);

  // Exercises
  const [showExerciseDialog, setShowExerciseDialog] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [exerciseForm, setExerciseForm] = useState({
    title: '', description: '', youtube_url: '', video_file: '', category: '', difficulty: 'beginner', body_parts: [], duration_minutes: 15
  });
  const [videoSource, setVideoSource] = useState('youtube');
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Courses
  const [showCourseDialog, setShowCourseDialog] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [courseForm, setCourseForm] = useState({
    title: '', description: '', exercise_ids: [], price: 29.99, is_subscription: false, category: '', difficulty: 'beginner', duration_weeks: 4
  });

  // Messages
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Community
  const [communityMessages, setCommunityMessages] = useState([]);

  useEffect(() => { fetchAllData(); }, []);

  const fetchAllData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [statsRes, exercisesRes, coursesRes, usersRes, aboutRes, testimonialsRes, conversationsRes, communityRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/exercises`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/courses`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/users`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/about`),
        fetch(`${API}/testimonials`),
        fetch(`${API}/messages/conversations`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/community/messages`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (exercisesRes.ok) setExercises(await exercisesRes.json());
      if (coursesRes.ok) setCourses(await coursesRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (aboutRes.ok) {
        const about = await aboutRes.json();
        setAboutSection(about);
        setAboutVideoSource(about.youtube_url ? 'youtube' : 'upload');
      }
      if (testimonialsRes.ok) setTestimonials(await testimonialsRes.json());
      if (conversationsRes.ok) setConversations(await conversationsRes.json());
      if (communityRes.ok) setCommunityMessages(await communityRes.json());
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  // Exercise handlers
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingVideo(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API}/upload/video`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (response.ok) {
        const data = await response.json();
        setExerciseForm({ ...exerciseForm, video_file: data.filename, youtube_url: '' });
        toast.success('Video uploaded successfully');
      } else { toast.error('Failed to upload video'); }
    } catch (error) { toast.error('Failed to upload video'); }
    finally { setUploadingVideo(false); }
  };

  const handleSaveExercise = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      const url = editingExercise ? `${API}/exercises/${editingExercise.id}` : `${API}/exercises`;
      const method = editingExercise ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...exerciseForm, body_parts: typeof exerciseForm.body_parts === 'string' ? exerciseForm.body_parts.split(',').map(s => s.trim()) : exerciseForm.body_parts })
      });
      if (response.ok) {
        toast.success(`Exercise ${editingExercise ? 'updated' : 'created'} successfully`);
        setShowExerciseDialog(false);
        setEditingExercise(null);
        setExerciseForm({ title: '', description: '', youtube_url: '', category: '', difficulty: 'beginner', body_parts: [], duration_minutes: 15 });
        fetchAllData();
      }
    } catch (error) { toast.error('Failed to save exercise'); }
  };

  const handleDeleteExercise = async (id) => {
    if (!window.confirm('Are you sure you want to delete this exercise?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/exercises/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { toast.success('Exercise deleted'); fetchAllData(); }
    } catch (error) { toast.error('Failed to delete exercise'); }
  };

  // Course handlers
  const handleSaveCourse = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/courses`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(courseForm)
      });
      if (response.ok) {
        toast.success('Course created successfully');
        setShowCourseDialog(false);
        setCourseForm({ title: '', description: '', exercise_ids: [], price: 29.99, is_subscription: false, category: '', difficulty: 'beginner', duration_weeks: 4 });
        fetchAllData();
      }
    } catch (error) { toast.error('Failed to save course'); }
  };

  const handleDeleteCourse = async (id) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/courses/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { toast.success('Course deleted successfully'); fetchAllData(); }
    } catch (error) { toast.error('Failed to delete course'); }
  };

  // About handlers
  const handleAboutVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAboutVideo(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API}/upload/video`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (response.ok) {
        const data = await response.json();
        setAboutSection({ ...aboutSection, video_file: data.filename, youtube_url: '' });
        toast.success('Video uploaded successfully');
      } else { toast.error('Failed to upload video'); }
    } catch (error) { toast.error('Failed to upload video'); }
    finally { setUploadingAboutVideo(false); }
  };

  const handleAboutImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAboutImage(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API}/upload/image`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (response.ok) {
        const data = await response.json();
        setAboutSection(prev => ({ ...prev, image_file: data.filename }));
        toast.success('Portrait image uploaded successfully');
      } else { toast.error('Failed to upload image'); }
    } catch (error) { toast.error('Failed to upload image'); }
    finally { setUploadingAboutImage(false); }
  };

  const handleSaveAbout = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/about`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(aboutSection)
      });
      if (response.ok) { toast.success('About section updated successfully'); fetchAllData(); }
    } catch (error) { toast.error('Failed to update about section'); }
  };

  // Testimonial handlers
  const handleSaveTestimonial = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/testimonials`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(testimonialForm)
      });
      if (response.ok) {
        toast.success('Testimonial added successfully');
        setShowTestimonialDialog(false);
        setTestimonialForm({ name: '', role: '', content: '', rating: 5, image_url: '' });
        fetchAllData();
      }
    } catch (error) { toast.error('Failed to save testimonial'); }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/testimonials/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { toast.success('Testimonial deleted'); fetchAllData(); }
    } catch (error) { toast.error('Failed to delete testimonial'); }
  };

  // Message handlers
  const fetchMessages = async (userId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/messages/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setMessages(await response.json());
    } catch (error) { toast.error('Failed to load messages'); }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.user_id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    setSendingMessage(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ receiver_id: selectedConversation.user_id, content: newMessage })
      });
      if (response.ok) { setNewMessage(''); fetchMessages(selectedConversation.user_id); fetchAllData(); }
    } catch (error) { toast.error('Failed to send message'); }
    finally { setSendingMessage(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4EFE6]">
      <nav className="bg-[#0B0B0B] text-white px-6 py-4">
        <div className='container mx-auto flex justify-between items-center'>
          <div className='text-2xl font-bold' style={{ fontFamily: 'Clash Display' }}>Admin Dashboard</div>
          <div className='flex gap-4 items-center'>
            <span>{user?.name}</span>
            <Button data-testid='admin-change-password-btn' onClick={() => setShowChangePassword(true)} variant='outline' className='rounded-full border-white text-white hover:bg-white/10'>
              <KeyRound className='w-4 h-4 mr-2' /> Change Password
            </Button>
            <Button data-testid='admin-logout-btn' onClick={handleLogout} variant='outline' className='rounded-full border-white text-white hover:bg-white/10'>
              <LogOut className='w-4 h-4 mr-2' /> Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className='container mx-auto px-6 py-12' data-testid='admin-dashboard'>
        <div className='grid md:grid-cols-5 gap-6 mb-12'>
          <Card className='p-6 bg-white rounded-2xl'>
            <Users className='w-10 h-10 text-[#0B0B0B] mb-4' />
            <div className='text-3xl font-bold text-[#0B0B0B] mb-1'>{stats.total_users || 0}</div>
            <div className='text-gray-600'>Total Users</div>
          </Card>
          <Card className='p-6 bg-white rounded-2xl'>
            <Activity className='w-10 h-10 text-[#0B0B0B] mb-4' />
            <div className='text-3xl font-bold text-[#0B0B0B] mb-1'>{stats.total_exercises || 0}</div>
            <div className='text-gray-600'>Exercises</div>
          </Card>
          <Card className='p-6 bg-white rounded-2xl'>
            <BookOpen className='w-10 h-10 text-[#0B0B0B] mb-4' />
            <div className='text-3xl font-bold text-[#0B0B0B] mb-1'>{stats.total_courses || 0}</div>
            <div className='text-gray-600'>Courses</div>
          </Card>
          <Card className='p-6 bg-white rounded-2xl'>
            <Crown className='w-10 h-10 text-[#0B0B0B] mb-4' />
            <div className='text-3xl font-bold text-[#0B0B0B] mb-1'>{stats.active_subscriptions || 0}</div>
            <div className='text-gray-600'>Active Subs</div>
          </Card>
          <Card className='p-6 bg-white rounded-2xl'>
            <TrendingUp className='w-10 h-10 text-[#0B0B0B] mb-4' />
            <div className='text-3xl font-bold text-[#0B0B0B] mb-1'>{stats.total_payments || 0}</div>
            <div className='text-gray-600'>Payments</div>
          </Card>
        </div>

        <Tabs defaultValue='exercises' className='w-full'>
          <TabsList className='mb-6'>
            <TabsTrigger value='exercises'>Exercises</TabsTrigger>
            <TabsTrigger value='courses'>Courses</TabsTrigger>
            <TabsTrigger value='subscriptions' data-testid='subscriptions-tab'>Subscriptions</TabsTrigger>
            <TabsTrigger value='users'>Users</TabsTrigger>
            <TabsTrigger value='messages'>Messages</TabsTrigger>
            <TabsTrigger value='community'>Community</TabsTrigger>
            <TabsTrigger value='content'>Content</TabsTrigger>
          </TabsList>

          <TabsContent value='exercises'>
            <AdminExercisesTab
              exercises={exercises}
              onCreateClick={() => { setEditingExercise(null); setShowExerciseDialog(true); }}
              onEditClick={(ex) => { setEditingExercise(ex); setExerciseForm(ex); setShowExerciseDialog(true); }}
              onDelete={handleDeleteExercise}
            />
          </TabsContent>

          <TabsContent value='courses'>
            <AdminCoursesTab courses={courses} onCreateClick={() => setShowCourseDialog(true)} onDelete={handleDeleteCourse} />
          </TabsContent>

          <TabsContent value='subscriptions'>
            <AdminSubscriptionsTab />
          </TabsContent>

          <TabsContent value='users'>
            <AdminUsersTab users={users} />
          </TabsContent>

          <TabsContent value='messages'>
            <AdminMessagesTab
              conversations={conversations}
              selectedConversation={selectedConversation}
              messages={messages}
              newMessage={newMessage}
              sendingMessage={sendingMessage}
              userId={user?.id}
              onSelectConversation={handleSelectConversation}
              onMessageChange={setNewMessage}
              onSendMessage={handleSendMessage}
            />
          </TabsContent>

          <TabsContent value='community'>
            <AdminCommunityTab communityMessages={communityMessages} onRefresh={fetchAllData} />
          </TabsContent>

          <TabsContent value='content'>
            <AdminContentTab
              aboutSection={aboutSection} setAboutSection={setAboutSection}
              aboutVideoSource={aboutVideoSource} setAboutVideoSource={setAboutVideoSource}
              uploadingAboutVideo={uploadingAboutVideo} uploadingAboutImage={uploadingAboutImage}
              onAboutVideoUpload={handleAboutVideoUpload} onAboutImageUpload={handleAboutImageUpload} onSaveAbout={handleSaveAbout}
              testimonials={testimonials}
              onShowTestimonialDialog={() => setShowTestimonialDialog(true)} onDeleteTestimonial={handleDeleteTestimonial}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Exercise Dialog */}
      <Dialog open={showExerciseDialog} onOpenChange={setShowExerciseDialog}>
        <DialogContent className='max-w-2xl' data-testid='exercise-dialog'>
          <DialogHeader>
            <DialogTitle>{editingExercise ? 'Edit' : 'Create'} Exercise</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveExercise} className='space-y-4'>
            <div>
              <Label>Title</Label>
              <Input value={exerciseForm.title} onChange={(e) => setExerciseForm({...exerciseForm, title: e.target.value})} required data-testid='exercise-title-input' />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={exerciseForm.description} onChange={(e) => setExerciseForm({...exerciseForm, description: e.target.value})} required data-testid='exercise-description-input' />
            </div>
            <div>
              <Label className='mb-2 block'>Video Source</Label>
              <div className='flex gap-4 mb-4'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input type='radio' name='videoSource' value='youtube' checked={videoSource === 'youtube'}
                    onChange={(e) => { setVideoSource(e.target.value); setExerciseForm({...exerciseForm, video_file: '', youtube_url: ''}); }} />
                  <span>YouTube URL</span>
                </label>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input type='radio' name='videoSource' value='upload' checked={videoSource === 'upload'}
                    onChange={(e) => { setVideoSource(e.target.value); setExerciseForm({...exerciseForm, video_file: '', youtube_url: ''}); }} />
                  <span>Upload Video</span>
                </label>
              </div>
              {videoSource === 'youtube' ? (
                <div>
                  <Label>YouTube URL</Label>
                  <Input value={exerciseForm.youtube_url || ''} onChange={(e) => setExerciseForm({...exerciseForm, youtube_url: e.target.value, video_file: ''})} placeholder='https://www.youtube.com/watch?v=...' data-testid='exercise-youtube-input' />
                </div>
              ) : (
                <div>
                  <Label>Upload Video File</Label>
                  <input type='file' accept='video/*' onChange={handleVideoUpload} disabled={uploadingVideo} className='w-full p-2 border rounded' data-testid='exercise-video-upload' />
                  {uploadingVideo && <p className='text-sm text-gray-500 mt-2'>Uploading...</p>}
                  {exerciseForm.video_file && <p className='text-sm text-green-600 mt-2'>Video uploaded: {exerciseForm.video_file}</p>}
                </div>
              )}
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label>Category</Label>
                <Input value={exerciseForm.category} onChange={(e) => setExerciseForm({...exerciseForm, category: e.target.value})} required />
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={exerciseForm.difficulty} onValueChange={(val) => setExerciseForm({...exerciseForm, difficulty: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value='beginner'>Beginner</SelectItem>
                    <SelectItem value='intermediate'>Intermediate</SelectItem>
                    <SelectItem value='advanced'>Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label>Body Parts (comma-separated)</Label>
                <Input value={Array.isArray(exerciseForm.body_parts) ? exerciseForm.body_parts.join(', ') : exerciseForm.body_parts} onChange={(e) => setExerciseForm({...exerciseForm, body_parts: e.target.value})} placeholder='knee, hip, lower back' required />
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input type='number' value={exerciseForm.duration_minutes} onChange={(e) => setExerciseForm({...exerciseForm, duration_minutes: parseInt(e.target.value)})} required />
              </div>
            </div>
            <Button type='submit' className='w-full bg-[#0B0B0B] text-white rounded-full' data-testid='save-exercise-btn'>
              {editingExercise ? 'Update' : 'Create'} Exercise
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Course Dialog */}
      <Dialog open={showCourseDialog} onOpenChange={setShowCourseDialog}>
        <DialogContent className='max-w-2xl' data-testid='course-dialog'>
          <DialogHeader><DialogTitle>Create Course</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveCourse} className='space-y-4'>
            <div>
              <Label>Title</Label>
              <Input value={courseForm.title} onChange={(e) => setCourseForm({...courseForm, title: e.target.value})} required data-testid='course-title-input' />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={courseForm.description} onChange={(e) => setCourseForm({...courseForm, description: e.target.value})} required data-testid='course-description-input' />
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label>Category</Label>
                <Input value={courseForm.category} onChange={(e) => setCourseForm({...courseForm, category: e.target.value})} required />
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={courseForm.difficulty} onValueChange={(val) => setCourseForm({...courseForm, difficulty: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value='beginner'>Beginner</SelectItem>
                    <SelectItem value='intermediate'>Intermediate</SelectItem>
                    <SelectItem value='advanced'>Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label>Price (ZAR)</Label>
                <Input type='number' step='0.01' value={courseForm.price} onChange={(e) => setCourseForm({...courseForm, price: parseFloat(e.target.value)})} required data-testid='course-price-input' />
              </div>
              <div>
                <Label>Duration (weeks)</Label>
                <Input type='number' value={courseForm.duration_weeks} onChange={(e) => setCourseForm({...courseForm, duration_weeks: parseInt(e.target.value)})} required />
              </div>
            </div>
            <div>
              <Label>Exercises</Label>
              <div className='max-h-40 overflow-y-auto border rounded p-2 space-y-1'>
                {exercises.map(ex => (
                  <label key={ex.id} className='flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer'>
                    <input type='checkbox' checked={courseForm.exercise_ids.includes(ex.id)}
                      onChange={(e) => {
                        if (e.target.checked) { setCourseForm({...courseForm, exercise_ids: [...courseForm.exercise_ids, ex.id]}); }
                        else { setCourseForm({...courseForm, exercise_ids: courseForm.exercise_ids.filter(id => id !== ex.id)}); }
                      }} />
                    <span className='text-sm'>{ex.title}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button type='submit' className='w-full bg-[#0B0B0B] text-white rounded-full' data-testid='save-course-btn'>Create Course</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Testimonial Dialog */}
      <Dialog open={showTestimonialDialog} onOpenChange={setShowTestimonialDialog}>
        <DialogContent className='max-w-lg'>
          <DialogHeader><DialogTitle>Add Testimonial</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveTestimonial} className='space-y-4'>
            <div><Label>Name</Label><Input value={testimonialForm.name} onChange={(e) => setTestimonialForm({...testimonialForm, name: e.target.value})} required /></div>
            <div><Label>Role/Title</Label><Input value={testimonialForm.role} onChange={(e) => setTestimonialForm({...testimonialForm, role: e.target.value})} placeholder='e.g., Physical Therapist, Client' required /></div>
            <div><Label>Testimonial Content</Label><Textarea value={testimonialForm.content} onChange={(e) => setTestimonialForm({...testimonialForm, content: e.target.value})} rows={4} required /></div>
            <div>
              <Label>Rating (1-5 stars)</Label>
              <Select value={testimonialForm.rating.toString()} onValueChange={(val) => setTestimonialForm({...testimonialForm, rating: parseInt(val)})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value='1'>1 Star</SelectItem><SelectItem value='2'>2 Stars</SelectItem><SelectItem value='3'>3 Stars</SelectItem><SelectItem value='4'>4 Stars</SelectItem><SelectItem value='5'>5 Stars</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Image URL (optional)</Label><Input value={testimonialForm.image_url} onChange={(e) => setTestimonialForm({...testimonialForm, image_url: e.target.value})} placeholder='https://example.com/image.jpg' /></div>
            <Button type='submit' className='w-full bg-[#0B0B0B] text-white rounded-full'>Add Testimonial</Button>
          </form>
        </DialogContent>
      </Dialog>
      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
    </div>
  );
}
